import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ImagePlus, X, Check, Loader2, Home as HomeIcon } from "lucide-react";
import Container from "@/components/Container";

const PROPERTY_TYPES = [
  "Detached house","Semi-detached house","Terraced house","End terrace","Bungalow",
  "Flat","Maisonette","Cottage","Townhouse","Mews house","HMO","Block of flats",
  "Commercial","Mixed-use","Land / plot","Farm / barn","Development site","Other",
];
const TENURES = ["Freehold","Leasehold","Share of freehold","Commonhold","Unknown"];
const CONDITIONS = [
  "Habitable","In need of modernisation","Full refurbishment required",
  "Derelict / uninhabitable","Fire / water damaged","Structural work needed","Newly renovated",
];
const REASONS = [
  "Probate","Distressed sale","Divorce","Relocation","Downsizing",
  "Investment exit","Financial pressure","Retirement","Portfolio release","Other",
];
const TIMELINES = ["As soon as possible","Within 1 month","1–3 months","3–6 months","Flexible / open"];
const MAX_PHOTOS = 12;

const STEPS = [
  { key: "you",       label: "You" },
  { key: "property",  label: "Property" },
  { key: "situation", label: "Situation" },
  { key: "photos",    label: "Photos" },
  { key: "review",    label: "Review" },
];

export default function Sell() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    property_address: "",
    property_type: "", tenure: "", bedrooms: "", bathrooms: "",
    condition: "", reason_for_sale: "",
    timeline: "", asking_price: "", budget: "",
    property_details: "",
  });
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setDirect = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ─── Photo upload ──────────────────────────────
  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const remaining = MAX_PHOTOS - images.length;
    if (remaining <= 0) { toast.error(`Maximum ${MAX_PHOTOS} photos.`); return; }
    const batch = files.slice(0, remaining);
    const placeholders = batch.map((f, i) => ({
      tempId: `t-${Date.now()}-${i}`, name: f.name, uploading: true, url: null,
    }));
    setImages((cur) => [...cur, ...placeholders]);
    for (let i = 0; i < batch.length; i++) {
      const file = batch[i];
      const temp = placeholders[i];
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/public/lead-photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setImages((cur) =>
          cur.map((im) => im.tempId === temp.tempId
            ? { ...im, url: data.url, path: data.path, uploading: false }
            : im)
        );
      } catch (err) {
        setImages((cur) => cur.filter((im) => im.tempId !== temp.tempId));
        toast.error(formatApiError(err.response?.data?.detail, `Upload failed: ${file.name}`));
      }
    }
  };
  const removePhoto = (tempId) => setImages((cur) => cur.filter((im) => im.tempId !== tempId));

  // ─── Step validation ──────────────────────────────
  const canAdvance = () => {
    if (step === 0) return form.name.trim() && form.email.trim().includes("@");
    if (step === 3) return !images.some((im) => im.uploading);
    return true;
  };
  const advance = () => {
    if (!canAdvance()) {
      if (step === 0) toast.error("Please enter a name and a valid email.");
      else if (step === 3) toast.error("Photos are still uploading — one moment.");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));
  const onKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && step < STEPS.length - 1) {
      e.preventDefault();
      advance();
    }
  };

  // ─── Submit ──────────────────────────────
  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required."); setStep(0); return; }
    setSubmitting(true);
    try {
      await api.post("/public/leads", {
        lead_type: "seller",
        source_page: "/sell",
        consent: true,
        ...form,
        image_urls: images.filter((im) => im.url).map((im) => im.url),
      });
      setSent(true);
      toast.success("Thank you. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ──────────────────────────────
  return (
    <div className="pt-36 pb-32">
      <Container className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
        {/* LEFT column — deliberately tight, matches wizard height on desktop */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <div className="flex items-center gap-4 mb-10">
              <span className="label-mono">No. 06 — Sell to Rubys</span>
              <span className="h-px w-10 bg-ruby" />
            </div>
            <h1 className="serif-display text-5xl md:text-6xl lg:text-[5.75rem] text-ink-700 !leading-[1.02]">
              Discreet.<br />
              Decisive.<br />
              <span className="serif-italic text-ruby">Direct.</span>
            </h1>
            <p className="mt-10 text-ink-400 !leading-[1.75] text-base lg:text-[1.05rem] max-w-md">
              We buy probate, distressed, derelict, vacant, and under-presented properties — quickly, cleanly, and on terms that respect the seller.
            </p>

            <div className="mt-12 space-y-5">
              {[
                ["Cash offers, no chains", "Structured or clean cash."],
                ["Completion in 14 days", "Where the title allows."],
                ["Difficult titles welcome", "Probate, short lease, unmortgageable."],
                ["Strict confidentiality", "Family matters handled as family matters."],
              ].map(([t, d], i) => (
                <div key={t} className="flex items-baseline gap-4 pb-4 border-b border-ink-700/8 last:border-b-0">
                  <span className="editorial-numeral text-lg text-ruby/80 shrink-0 w-8">{String(i+1).padStart(2,"0")}</span>
                  <div>
                    <p className="serif-light text-lg text-ink-700 !leading-[1.3]">{t}</p>
                    <p className="text-xs text-ink-100 mt-1">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Wizard */}
        <div className="lg:col-span-7">
          {sent ? (
            <ThankYou />
          ) : (
            <div
              className="surface-paper p-8 md:p-12 lg:p-14 border-t-2 border-ruby"
              data-testid="lead-form-seller"
              onKeyDown={onKeyDown}
            >
              {/* Progress rail */}
              <Progress step={step} onJump={(i) => i < step && setStep(i)} />

              <div className="mt-12 min-h-[460px]">
                {step === 0 && (
                  <Step n={1} title="First, let's find you." subtitle="So we know who we're writing back to.">
                    <TxtField label="Full name *" testid="seller-name" value={form.name} onChange={set("name")} autoFocus />
                    <TxtField label="Email *" testid="seller-email" type="email" value={form.email} onChange={set("email")} />
                    <TxtField label="Phone" testid="seller-phone" value={form.phone} onChange={set("phone")} placeholder="(optional)" wide />
                  </Step>
                )}

                {step === 1 && (
                  <Step n={2} title="Where is the property?" subtitle="Just the essentials — we'll come to you for the rest.">
                    <TxtField label="Property address" testid="seller-property_address" value={form.property_address} onChange={set("property_address")} placeholder="Street, town, postcode" wide autoFocus />
                    <ChipSelect label="Property type" testid="seller-property_type"
                      value={form.property_type} onChange={(v) => setDirect("property_type", v)} options={PROPERTY_TYPES} wide />
                    <ChipSelect label="Tenure" testid="seller-tenure"
                      value={form.tenure} onChange={(v) => setDirect("tenure", v)} options={TENURES} wide />
                    <TxtField label="Bedrooms" testid="seller-bedrooms" type="number" value={form.bedrooms} onChange={set("bedrooms")} />
                    <TxtField label="Bathrooms" testid="seller-bathrooms" type="number" value={form.bathrooms} onChange={set("bathrooms")} />
                  </Step>
                )}

                {step === 2 && (
                  <Step n={3} title="Tell us the situation." subtitle="Condition, reason and timing — plus the numbers if you have them.">
                    <ChipSelect label="Condition" testid="seller-condition"
                      value={form.condition} onChange={(v) => setDirect("condition", v)} options={CONDITIONS} wide />
                    <ChipSelect label="Reason for sale" testid="seller-reason_for_sale"
                      value={form.reason_for_sale} onChange={(v) => setDirect("reason_for_sale", v)} options={REASONS} wide />
                    <ChipSelect label="Preferred timeline" testid="seller-timeline"
                      value={form.timeline} onChange={(v) => setDirect("timeline", v)} options={TIMELINES} wide />
                    <TxtField label="Asking price" testid="seller-asking_price" value={form.asking_price} onChange={set("asking_price")} placeholder="£" />
                    <TxtField label="Minimum acceptable" testid="seller-budget" value={form.budget} onChange={set("budget")} placeholder="£" />
                  </Step>
                )}

                {step === 3 && (
                  <Step n={4} title="Show us the property." subtitle={`Up to ${MAX_PHOTOS} photos — interior, exterior, problems and character. All optional.`}>
                    <div className="w-full" data-testid="seller-photos-block">
                      <div className="flex items-baseline justify-between mb-4">
                        <p className="label-mono-mute">Photos</p>
                        <p className="text-[0.7rem] text-ink-100 font-mono">{images.length}/{MAX_PHOTOS}</p>
                      </div>

                      {images.length > 0 && (
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4" data-testid="seller-photos-grid">
                          {images.map((im) => (
                            <div key={im.tempId} className="relative aspect-[4/3] bg-cream-200 border border-ink-700/10 overflow-hidden group" data-testid={`seller-photo-${im.tempId}`}>
                              {im.uploading ? (
                                <div className="w-full h-full flex flex-col items-center justify-center text-ink-100 gap-2">
                                  <Loader2 size={20} className="animate-spin text-ruby" />
                                  <p className="text-[0.6rem] font-mono truncate max-w-full px-2">{im.name}</p>
                                </div>
                              ) : (
                                <img src={im.url} alt={im.name} className="w-full h-full object-cover" />
                              )}
                              {!im.uploading && (
                                <button
                                  type="button"
                                  onClick={() => removePhoto(im.tempId)}
                                  className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center bg-ink-700 text-cream-100 hover:bg-ruby transition-colors opacity-0 group-hover:opacity-100"
                                  data-testid={`seller-photo-remove-${im.tempId}`}
                                  aria-label="Remove photo"
                                >
                                  <X size={12} strokeWidth={2} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {images.length < MAX_PHOTOS && (
                        <label
                          className="flex flex-col items-center justify-center py-10 border border-dashed border-ink-700/25 hover:border-ruby text-ink-700 hover:text-ruby cursor-pointer transition-colors"
                          data-testid="seller-photo-picker-label"
                        >
                          <ImagePlus size={26} strokeWidth={1.2} />
                          <p className="mt-3 text-sm">Click to add photos</p>
                          <p className="text-[0.7rem] text-ink-100 mt-1 font-mono uppercase tracking-[0.16em]">JPG · PNG · WEBP · HEIC · 8MB each</p>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                            multiple
                            className="hidden"
                            onChange={onPickPhotos}
                            data-testid="seller-photo-input"
                          />
                        </label>
                      )}
                    </div>
                  </Step>
                )}

                {step === 4 && (
                  <Step n={5} title="Anything else, and we're done." subtitle="A quick review — tap any bar above to edit a step.">
                    <div className="w-full">
                      <label className="block">
                        <span className="label-mono-mute">Notes for our team</span>
                        <textarea
                          value={form.property_details}
                          onChange={set("property_details")}
                          rows={4}
                          data-testid="seller-property_details"
                          className="editorial-input mt-2 resize-none"
                          placeholder="Access issues, tenants, planning history, urgency, sensitivities…"
                        />
                      </label>

                      <div className="mt-8 border-t border-ink-700/10 pt-6" data-testid="seller-review">
                        <p className="label-mono-mute mb-4">Review</p>
                        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                          <ReviewItem k="Name" v={form.name} />
                          <ReviewItem k="Email" v={form.email} />
                          <ReviewItem k="Phone" v={form.phone} />
                          <ReviewItem k="Address" v={form.property_address} wide />
                          <ReviewItem k="Type" v={form.property_type} />
                          <ReviewItem k="Tenure" v={form.tenure} />
                          <ReviewItem k="Bedrooms" v={form.bedrooms} />
                          <ReviewItem k="Bathrooms" v={form.bathrooms} />
                          <ReviewItem k="Condition" v={form.condition} />
                          <ReviewItem k="Reason" v={form.reason_for_sale} />
                          <ReviewItem k="Timeline" v={form.timeline} />
                          <ReviewItem k="Asking" v={form.asking_price ? `£${form.asking_price}` : ""} />
                          <ReviewItem k="Minimum" v={form.budget ? `£${form.budget}` : ""} />
                          <ReviewItem k="Photos" v={images.length ? `${images.length} attached` : ""} />
                        </dl>
                      </div>
                    </div>
                  </Step>
                )}
              </div>

              {/* Navigation */}
              <div className="mt-10 pt-6 border-t border-ink-700/10 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  className="text-ink-100 hover:text-ink-700 text-[0.7rem] uppercase tracking-[0.18em] font-mono inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="seller-wizard-back"
                >
                  <ArrowLeft size={13} strokeWidth={1.5} /> Back
                </button>

                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={advance}
                    className="btn-ruby-solid"
                    data-testid="seller-wizard-next"
                  >
                    Continue <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    className="btn-ruby-solid disabled:opacity-50"
                    data-testid="seller-form-submit"
                  >
                    {submitting ? "Sending…" : "Submit property"} <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}

/* ────── UI primitives (kept in-file to preserve editorial control) ────── */

function Progress({ step, onJump }) {
  return (
    <div data-testid="seller-wizard-progress">
      {/* Top row: editorial step counter */}
      <div className="flex items-baseline justify-between">
        <p className="label-mono">
          Step <span className="text-ruby">{String(step + 1).padStart(2, "0")}</span>
          <span className="text-ink-100"> / {String(STEPS.length).padStart(2, "0")}</span>
        </p>
        <p className="text-[0.65rem] uppercase tracking-[0.22em] font-mono text-ink-100">
          {STEPS[step].label}
        </p>
      </div>

      {/* Rail — 5 equal-width segments, tap to jump back */}
      <div className="mt-4 flex gap-1.5">
        {STEPS.map((s, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onJump(i)}
              disabled={i > step}
              aria-label={`Go to step ${i + 1} — ${s.label}`}
              data-testid={`seller-wizard-step-${i}`}
              className={`flex-1 h-[3px] transition-colors duration-500 ${
                active ? "bg-ruby"
                  : done ? "bg-ink-700/60 hover:bg-ruby cursor-pointer"
                  : "bg-ink-700/10 cursor-default"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

function Step({ n, title, subtitle, children }) {
  return (
    <div data-testid="seller-wizard-step-content" className="animate-fade-in">
      <span className="editorial-numeral text-lg text-ruby/80">
        {String(n).padStart(2, "0")}
      </span>
      <h3 className="serif-display text-3xl md:text-[2.65rem] text-ink-700 !leading-[1.1] mt-3">
        {title}
      </h3>
      {subtitle && <p className="mt-4 text-ink-100 !leading-[1.7] max-w-lg text-[0.95rem]">{subtitle}</p>}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
        {children}
      </div>
    </div>
  );
}

function TxtField({ label, testid, value, onChange, type = "text", placeholder, autoFocus, wide }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="label-mono-mute">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testid}
        className="editorial-input mt-2"
      />
    </label>
  );
}

/**
 * ChipSelect — a row of clickable chips that acts like a select, but feels
 * interactive and modern. On mobile it stays as chips (wraps) and is much
 * quicker to tap than a native <select>.
 */
function ChipSelect({ label, testid, value, onChange, options, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <span className="label-mono-mute">{label}</span>
      <div className="mt-3 flex flex-wrap gap-2" data-testid={testid}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(active ? "" : opt)}
              data-testid={`${testid}-opt-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className={`px-3 py-1.5 text-[0.78rem] border transition-colors ${
                active
                  ? "border-ruby bg-ruby text-cream-100"
                  : "border-ink-700/20 text-ink-700 hover:border-ruby hover:text-ruby"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReviewItem({ k, v, wide }) {
  return (
    <div className={wide ? "col-span-2 md:col-span-3" : ""}>
      <dt className="text-[0.6rem] uppercase tracking-[0.15em] text-ink-100 font-mono">{k}</dt>
      <dd className={`mt-0.5 ${v ? "text-ink-700" : "text-ink-100/60"}`}>{v || "—"}</dd>
    </div>
  );
}

function ThankYou() {
  return (
    <div className="surface-paper p-12 lg:p-16 border-t-2 border-ruby" data-testid="seller-form-sent">
      <div className="w-14 h-14 border border-ruby flex items-center justify-center">
        <Check className="text-ruby" size={22} strokeWidth={1.3} />
      </div>
      <p className="label-mono mt-10">Received</p>
      <h3 className="serif-display text-5xl lg:text-6xl text-ink-700 mt-6 !leading-[1.05]">Thank you.</h3>
      <p className="text-ink-400 mt-6 !leading-[1.8] max-w-md text-base lg:text-lg">
        A member of the Rubys.Group acquisitions team will be in touch within one working day.
      </p>
      <div className="mt-10 inline-flex items-center gap-2 text-ink-100 text-xs font-mono uppercase tracking-[0.18em]">
        <HomeIcon size={12} strokeWidth={1.4} /> Rubys.Group · acquisitions
      </div>
    </div>
  );
}
